/**
 * Registrations tab filters (GET /notify/notification_center/registrations).
 * Other tables ignore these query keys.
 */

import { REGISTRATION_TRACKER_STATUS_OPTIONS } from "constants/registrationTrackerStatuses";

const EMPTY_ARRAY = Object.freeze([]);

/** Default Status filter: every RT status except these two. */
const NC_REGISTRATIONS_DEFAULT_EXCLUDED_STATUSES = new Set([
  "completed",
  "registration_issued",
]);

export const NOTIFICATION_CENTER_REGISTRATIONS_DEFAULT_STATUSES = Object.freeze(
  REGISTRATION_TRACKER_STATUS_OPTIONS.map((option) => option.value).filter(
    (value) => !NC_REGISTRATIONS_DEFAULT_EXCLUDED_STATUSES.has(value)
  )
);

export function getNotificationCenterRegistrationsDefaultStatuses() {
  return [...NOTIFICATION_CENTER_REGISTRATIONS_DEFAULT_STATUSES];
}

function toTrimmedString(value) {
  if (value == null) return "";
  return String(value).trim();
}

/** Server expects `YYYY-MM-DD`; store dates in that shape to avoid timezone drift. */
export function toNotificationCenterFilterDate(value) {
  if (value == null || value === "") return null;

  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return null;
    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, "0");
    const day = String(value.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }

  const text = toTrimmedString(value);
  if (/^\d{4}-\d{2}-\d{2}$/.test(text)) {
    return text;
  }

  const parsed = new Date(text);
  return Number.isNaN(parsed.getTime())
    ? null
    : toNotificationCenterFilterDate(parsed);
}

export function normalizeRegistrationsCompanyFilter(value) {
  const text = toTrimmedString(value);
  return text === "" ? null : text;
}

export function normalizeRegistrationsTypeFilter(value) {
  const text = toTrimmedString(value);
  return text === "" ? null : text;
}

export function normalizeRegistrationsJurisdictionFilter(value) {
  if (!Array.isArray(value)) return [];
  const seen = new Set();
  const result = [];
  for (const item of value) {
    const text = toTrimmedString(item);
    if (text === "" || seen.has(text)) continue;
    seen.add(text);
    result.push(text);
  }
  return result;
}

export const normalizeRegistrationsMultiSelectFilter =
  normalizeRegistrationsJurisdictionFilter;

export function normalizeRegistrationsOrderDateFilter(value) {
  if (value == null) return null;

  const from = toNotificationCenterFilterDate(value.from ?? value.start ?? null);
  const to = toNotificationCenterFilterDate(value.to ?? value.end ?? null);

  if (!from && !to) return null;
  if (from && to && from > to) {
    return { from: to, to: from };
  }
  return { from: from ?? null, to: to ?? null };
}

export const normalizeRegistrationsDateFilter =
  normalizeRegistrationsOrderDateFilter;

/** One value at a time. Minus = newest first. Filter names differ from sort names. */
export const REGISTRATIONS_ORDERING = Object.freeze({
  LAST_STATUS_NEWEST: "-date_last_status_changed",
  LAST_STATUS_OLDEST: "date_last_status_changed",
  ESTIMATED_NEWEST: "-estimated_date_of_registration",
  ESTIMATED_OLDEST: "estimated_date_of_registration",
});

const REGISTRATIONS_ORDERING_VALUES = new Set(
  Object.values(REGISTRATIONS_ORDERING)
);

export function normalizeRegistrationsOrdering(value) {
  const text = toTrimmedString(value);
  return REGISTRATIONS_ORDERING_VALUES.has(text) ? text : null;
}

export function getRegistrationsOrderingForField(ordering, field) {
  const current = normalizeRegistrationsOrdering(ordering);
  if (!current || !field) return null;
  if (current === field || current === `-${field}`) return current;
  return null;
}

/**
 * @param {Record<string, unknown>} filters
 * @returns {Record<string, string | string[]>}
 */
export function getNotificationCenterRegistrationsQueryParams(filters) {
  const params = {};
  if (!filters) return params;

  const companyId = normalizeRegistrationsCompanyFilter(
    filters.registrationsCompanyFilter
  );
  if (companyId) {
    params.company_id = companyId;
  }

  const orderDate = normalizeRegistrationsOrderDateFilter(
    filters.registrationsOrderDateFilter
  );
  if (orderDate) {
    // A single day uses order_date; the server ignores the range when it is set.
    if (orderDate.from && orderDate.from === orderDate.to) {
      params.order_date = orderDate.from;
    } else {
      if (orderDate.from) params.order_date_from = orderDate.from;
      if (orderDate.to) params.order_date_to = orderDate.to;
    }
  }

  const jurisdictions = normalizeRegistrationsJurisdictionFilter(
    filters.registrationsJurisdictionFilter
  );
  if (jurisdictions.length > 0) {
    params.jurisdiction = jurisdictions;
  }

  const typeOfRegistration = normalizeRegistrationsTypeFilter(
    filters.registrationsTypeOfRegistrationFilter
  );
  if (typeOfRegistration) {
    params.type_of_registration = typeOfRegistration;
  }

  const subcategories = normalizeRegistrationsMultiSelectFilter(
    filters.registrationsSubcategoryFilter
  );
  if (
    String(typeOfRegistration).toUpperCase() === "EPR" &&
    subcategories.length > 0
  ) {
    params.subcategory = subcategories;
  }

  const statuses = normalizeRegistrationsMultiSelectFilter(
    filters.registrationsStatusFilter
  );
  if (statuses.length > 0) {
    params.status = statuses;
  }

  const lastStatusChanged = normalizeRegistrationsDateFilter(
    filters.registrationsDateLastStatusChangedFilter
  );
  if (lastStatusChanged) {
    if (
      lastStatusChanged.from &&
      lastStatusChanged.from === lastStatusChanged.to
    ) {
      params.date_last_status_changed = lastStatusChanged.from;
    } else {
      if (lastStatusChanged.from) {
        params.date_last_status_changed_from = lastStatusChanged.from;
      }
      if (lastStatusChanged.to) {
        params.date_last_status_changed_to = lastStatusChanged.to;
      }
    }
  }

  const registratorIds = normalizeRegistrationsMultiSelectFilter(
    filters.registrationsRegistratorFilter
  );
  if (registratorIds.length > 0) {
    params.registrator_id = registratorIds;
  }

  const estimatedRegistrationDate = normalizeRegistrationsDateFilter(
    filters.registrationsEstimatedRegistrationDateFilter
  );
  if (estimatedRegistrationDate) {
    if (
      estimatedRegistrationDate.from &&
      estimatedRegistrationDate.from === estimatedRegistrationDate.to
    ) {
      params.estimated_registration_date = estimatedRegistrationDate.from;
    } else {
      if (estimatedRegistrationDate.from) {
        params.estimated_registration_date_from =
          estimatedRegistrationDate.from;
      }
      if (estimatedRegistrationDate.to) {
        params.estimated_registration_date_to = estimatedRegistrationDate.to;
      }
    }
  }

  const ordering = normalizeRegistrationsOrdering(filters.registrationsOrdering);
  if (ordering) {
    params.ordering = ordering;
  }

  return params;
}

/** Stable key so in-flight de-duplication treats different filters as different requests. */
export function getNotificationCenterRegistrationsFilterSignature(filters) {
  const params = getNotificationCenterRegistrationsQueryParams(filters);
  const keys = Object.keys(params).sort();
  if (keys.length === 0) return "none";

  return keys
    .map((key) => {
      const value = params[key];
      return Array.isArray(value)
        ? `${key}=${[...value].sort().join("|")}`
        : `${key}=${value}`;
    })
    .join("&");
}

function resolveRowJurisdiction(row) {
  const code = [
    row?.jurisdiction_code,
    row?.jurisdictionCode,
    row?.jurisdiction,
    row?.state_code,
    row?.country_code,
  ]
    .map(toTrimmedString)
    .find((candidate) => candidate !== "");

  const label = toTrimmedString(row?.country_jurisdiction) || code || "";
  if (!label) return null;

  // Rows may carry only the display name; the code is resolved later from the
  // countries / US states dictionaries.
  return { value: code || null, label };
}

function looksLikeJurisdictionCode(value) {
  return /^[A-Z]{2,3}$/.test(value);
}

function buildNameToCodeMap(entries) {
  const map = new Map();
  for (const entry of entries ?? EMPTY_ARRAY) {
    const name = toTrimmedString(entry?.name);
    const code = toTrimmedString(entry?.code);
    if (name && code) {
      map.set(name.toLowerCase(), code);
    }
  }
  return map;
}

/**
 * The server filters by country code (DEU) or US state code (CO), while rows
 * expose a display name such as "Germany" or "USA (Texas)".
 */
export function resolveNotificationCenterJurisdictionOptions(
  options,
  { countries, states } = {}
) {
  const countryByName = buildNameToCodeMap(countries);
  const stateByName = buildNameToCodeMap(states);

  const resolved = [];
  const seen = new Set();

  for (const option of options ?? EMPTY_ARRAY) {
    const label = toTrimmedString(option?.label);
    const explicitCode = toTrimmedString(option?.value);

    let code = explicitCode;

    if (!code) {
      const insideParentheses = label.match(/\(([^)]+)\)\s*$/)?.[1];
      const outsideParentheses = label.replace(/\s*\([^)]*\)\s*$/, "").trim();

      const parenthesised = insideParentheses?.trim() ?? "";

      code =
        (parenthesised
          ? (stateByName.get(parenthesised.toLowerCase()) ??
            (looksLikeJurisdictionCode(parenthesised) ? parenthesised : undefined))
          : undefined) ??
        countryByName.get(label.toLowerCase()) ??
        countryByName.get(outsideParentheses.toLowerCase()) ??
        stateByName.get(label.toLowerCase()) ??
        (looksLikeJurisdictionCode(label) ? label : "");
    }

    if (!code || seen.has(code)) continue;
    seen.add(code);
    resolved.push({ value: code, label: label || code });
  }

  return resolved.sort((a, b) => String(a.label).localeCompare(String(b.label)));
}

function resolveRowCompany(row) {
  const value = toTrimmedString(row?.company_id ?? row?.companyId);
  if (!value) return null;
  return { value, label: toTrimmedString(row?.company) || value };
}

function resolveRowTypeOfRegistration(row) {
  const value = [row?.type_of_registration_code, row?.type_of_registration]
    .map(toTrimmedString)
    .find((candidate) => candidate !== "");

  if (!value) return null;
  return {
    value,
    label: toTrimmedString(row?.type_of_registration) || value,
  };
}

function optionKey(option) {
  return option?.value != null && String(option.value).trim() !== ""
    ? `value:${option.value}`
    : `label:${toTrimmedString(option?.label).toLowerCase()}`;
}

function mergeOptions(previous, incoming) {
  const byKey = new Map();
  for (const option of [...(previous ?? EMPTY_ARRAY), ...incoming]) {
    if (!option) continue;
    const key = optionKey(option);
    if (key === "label:") continue;
    byKey.set(key, option);
  }
  return [...byKey.values()].sort((a, b) =>
    String(a.label).localeCompare(String(b.label))
  );
}

/**
 * Options come from loaded rows, so they must accumulate: once a filter is
 * applied the response no longer contains the other values.
 */
export function collectNotificationCenterRegistrationsFilterOptions(
  items,
  previous
) {
  const rows = Array.isArray(items) ? items : EMPTY_ARRAY;

  const companies = [];
  const jurisdictions = [];
  const types = [];

  for (const row of rows) {
    const company = resolveRowCompany(row);
    if (company) companies.push(company);

    const jurisdiction = resolveRowJurisdiction(row);
    if (jurisdiction) jurisdictions.push(jurisdiction);

    const type = resolveRowTypeOfRegistration(row);
    if (type) types.push(type);
  }

  return {
    companies: mergeOptions(previous?.companies, companies),
    jurisdictions: mergeOptions(previous?.jurisdictions, jurisdictions),
    types: mergeOptions(previous?.types, types),
  };
}

export function createEmptyRegistrationsFilterOptions() {
  return { companies: [], jurisdictions: [], types: [] };
}

function resolveIdNameOptions(items) {
  return mergeOptions(
    [],
    (items ?? EMPTY_ARRAY).map((item) => {
      const value = toTrimmedString(item?.id);
      if (!value) return null;
      return {
        value,
        label: toTrimmedString(item?.name) || value,
      };
    })
  );
}

function resolveCodeNameOptions(items) {
  return mergeOptions(
    [],
    (items ?? EMPTY_ARRAY).map((item) => {
      const value = toTrimmedString(item?.code);
      if (!value) return null;
      return {
        value,
        label: toTrimmedString(item?.name) || value,
      };
    })
  );
}

export function resolveNotificationCenterCompanyOptions(companies) {
  return resolveIdNameOptions(companies);
}

export function resolveNotificationCenterCountryOptions(countries) {
  return resolveCodeNameOptions(countries);
}

export function resolveNotificationCenterRegistrationTypeOptions(
  types,
  fallback = []
) {
  return mergeOptions(fallback, resolveCodeNameOptions(types));
}

export function resolveNotificationCenterSubcategoryOptions(types) {
  const eprType = (types ?? EMPTY_ARRAY).find(
    (type) => toTrimmedString(type?.code).toUpperCase() === "EPR"
  );
  return mergeOptions(
    [],
    (eprType?.categories ?? EMPTY_ARRAY).map((category) => {
      const value = toTrimmedString(category?.code);
      if (!value) return null;
      return {
        value,
        label: toTrimmedString(category?.name) || value,
      };
    })
  );
}

export function resolveNotificationCenterRegistratorOptions(registrators) {
  return resolveIdNameOptions(registrators);
}

function normalizeRegistratorCountryCode(item) {
  if (item == null) return "";
  if (typeof item === "object") {
    return toTrimmedString(item.code ?? item.value);
  }
  return toTrimmedString(item);
}

/** `null` = no registrar selected, use the shared countries list. */
export function collectSelectedRegistratorCountryCodes(
  registrators,
  selectedIds
) {
  const ids = normalizeRegistrationsMultiSelectFilter(selectedIds);
  if (ids.length === 0) {
    return null;
  }

  const selected = new Set(ids);
  const seen = new Set();
  const codes = [];

  for (const person of registrators ?? EMPTY_ARRAY) {
    const id = toTrimmedString(person?.id);
    if (!id || !selected.has(id)) continue;
    for (const item of person?.countries ?? EMPTY_ARRAY) {
      const code = normalizeRegistratorCountryCode(item);
      if (!code || seen.has(code)) continue;
      seen.add(code);
      codes.push(code);
    }
  }

  return codes;
}

export function resolveNotificationCenterJurisdictionOptionsByRegistrators({
  countries,
  registrators,
  selectedRegistratorIds,
} = {}) {
  const allowedCodes = collectSelectedRegistratorCountryCodes(
    registrators,
    selectedRegistratorIds
  );
  if (allowedCodes == null) {
    return resolveNotificationCenterCountryOptions(countries);
  }

  const countryByCode = new Map();
  for (const country of countries ?? EMPTY_ARRAY) {
    const code = toTrimmedString(country?.code);
    if (code) {
      countryByCode.set(code, country);
    }
  }

  return resolveNotificationCenterCountryOptions(
    allowedCodes.map((code) => countryByCode.get(code) || { code, name: code })
  );
}

export function pruneRegistrationsJurisdictionFilter(selected, allowedOptions) {
  const current = normalizeRegistrationsJurisdictionFilter(selected);
  const allowed = new Set(
    (allowedOptions ?? EMPTY_ARRAY)
      .map((option) => toTrimmedString(option?.value))
      .filter(Boolean)
  );
  return current.filter((code) => allowed.has(code));
}
